import type { TierSelection } from '@/lib/mapping';
import { bundleRefs as importedBundleRefs } from '@/lib/bundleRefs';

export const config = Object.freeze({
  paymentMethods: {
    CREDITCARD: 'creditcard',
    SEPA: 'directdebit',
    PAYPAL: 'paypal',
    KLARNA: 'sofort',
    VOUCHER_PAYMENT: 'Gutscheinzahlung',
    NOT_SET: 'notset'
  },
  routes: {
    product: '/produkt',
    address: '/adresse',
    login: '/anmelden',
    register: '/registrieren',
    supporter: '/supporter',
    payment: '/bezahlen',
    confirmation: '/danke',
    approve: '/approve',
    error: '/fehler'
  },
  subscriptionGroups: {
    pur: 'pur',
    supporter: 'supporter'
  },
  supporterPeriods: {
    single: 'single',
    monthly: 'monthly',
    yearly: 'yearly'
  },
  development: {
    supporterRefs: {
      '001PB879NLLGY4QMT0': 'single',
      '001PBMTARFFD442LG1': 'monthly',
      '001PB1L1M6J28YW231': 'yearly'
    },
    bundleRefs: importedBundleRefs.development
  },
  production: {
    supporterRefs: {
      '001PBKIP0MRZ3YPJ80': 'single',
      '001PBOSKDO7X77G611': 'monthly',
      '001PBB14NB7VMNQBE2': 'yearly'
    },
    bundleRefs: importedBundleRefs.production
  },
  bundleNames: {
    pur: 'derStandard.at PUR',
    purAndPrint: 'derStandard.at PUR + STANDARD Wochenende',
    purDaily: 'derStandard.at PUR Tagesabo',
    purForSubscribers: 'derStandard.at PUR-Kombi für Abonnenten',
    purAndPrintYearly: 'derStandard.at PUR-Kombi Jahresabo',
    purYearly: 'derStandard.at PUR Jahresabo',
    supporterSingle: 'Einmalige Unterstützung',
    supporterMonthly: 'Monatliche Unterstützung',
    supporterYearly: 'Jährliche Unterstützung',
    smart: 'derStandard Smart ohne PUR', // TODO: change to 'derStandard Smart' on launch
    smartPlusPur: 'derStandard Smart' // TODO: change to 'derStandard Smart + PUR' on launch
  },
  biPixel: {
    mediaTypes: {
      androidApp: 8,
      iphoneApp: 5,
      mobile: 2,
      web: 1
    }
  },
  analytics: {
    trackingID: import.meta.env.NODE_ENV === 'development' ? 'UA-000000000-0' : 'UA-126846403-7'
  },
  cookie: {
    consent: 'DSGVO_ZUSAGE_V1',
    domain: import.meta.env.VITE_APP_COOKIE_DOMAIN
  },
  voucher: {
    minLength: 8
  }
} as const);

export const reusablePaymentMethods = Object.freeze([config.paymentMethods.CREDITCARD, config.paymentMethods.PAYPAL] as const);

/**
 * List of countries eligible for purchase
 *
 * key: ISO-3166-1 alpha 3 code (see https://www.wikiwand.com/en/ISO_3166-1#/Current_codes)
 * value: Country name in German
 */
export const countryList = Object.freeze({
  '': 'Bitte wählen Sie ein Land aus',
  // Priorized countries
  AUT: 'Österreich',
  DEU: 'Deutschland',
  CHE: 'Schweiz',
  USA: 'USA',

  // Other countries, alpahabetically sorted by value
  BEL: 'Belgien',
  BIH: 'Bosnien und Herzegowina',
  BRA: 'Brasilien',
  BGR: 'Bulgarien',
  DNK: 'Dänemark',
  EST: 'Estland',
  FIN: 'Finnland',
  FRA: 'Frankreich',
  GRC: 'Griechenland',
  IRE: 'Irland',
  ITA: 'Italien',
  CAN: 'Kanada',
  HRV: 'Kroatien',
  LVA: 'Lettland',
  LTU: 'Litauen',
  LUX: 'Luxemburg',
  MLT: 'Malta',
  NLD: 'Niederlande',
  NOR: 'Norwegen',
  POL: 'Polen',
  PRT: 'Portugal',
  ROU: 'Rumänien',
  SWE: 'Schweden',
  SVK: 'Slowakei',
  SVN: 'Slowenien',
  THA: 'Thailand',
  TUR: 'Türkei',
  ESP: 'Spanien',
  CZE: 'Tschechien',
  UKR: 'Ukraine',
  HUN: 'Ungarn',
  GBR: 'Vereinigtes Königreich',
  CYP: 'Zypern'
} as const);

export const salutationList = Object.freeze({
  '': 'Anrede',
  female: 'Frau',
  male: 'Herr'
} as const);

export const titleList = Object.freeze({
  '': 'Bitte wählen',
  bachelor: 'Bachelor',
  bakkalareaut: 'Bakkalaurea/Bakkalaureus',
  diplom: 'Diplom-Ingenieur:in',
  magister: 'Magistra/Magister',
  master: 'Master',
  doktor: 'Doktor:in'
} as const);

const env = (import.meta.env.VITE_APP_ENVIRONMENT || 'development') as 'development' | 'production';
export const supporterRefs = Object.freeze('supporterRefs' in config[env] ? config[env].supporterRefs : {});

export const supporterTiers: readonly TierSelection[] = Object.freeze(['single', 'monthly', 'yearly']);

export const isSupporterTier = (value: unknown): value is TierSelection => {
  return supporterTiers.includes(value as TierSelection);
};

export const supporterPeriodAliases = Object.freeze({
  single: ['single', 'einmalig'],
  monthly: ['monthly', 'monatlich'],
  yearly: ['yearly', 'jaehrlich', 'jahrlich', 'jährlich']
} as const);

export const supporterRoutes: Readonly<Record<string, string>> = Object.freeze({
  single: 'einmalige-unterstuetzung',
  monthly: 'monatliche-unterstuetzung',
  yearly: 'jaehrliche-unterstuetzung'
} as const);

export const customOrderUTMParameters = ['utmTerm', 'utmContent', 'utmSource', 'utmMedium', 'utmCampaign'];
